import { defineForm } from '@/components/Forms/FormBase';
import { defineComponent, nextTick, type PropType } from 'vue';
import type { ClientFormData } from './Client.dto';
import {
	NForm,
	NFormItem,
	NFormItemGi,
	NGrid,
	NInput,
	NRadio,
	NRadioGroup,
	NSelect,
	type FormRules,
} from 'naive-ui';
import { AppInputCpf, AppInputDate, AppInputMoney, AppInputCnpj } from '@/components';
import { ContactForm } from '@/components/Forms';
import { PersonType } from '@/constants';
import { Gender } from '@/constants/Gender';
import { nationalityOptions } from '@/constants/nationalityOptions';
import { PublicService } from '@/services/publicService';
import type { PhoneConflictInfo } from '@/schemas';
import { FormValidations } from '@/utils';

// No modo "só nome e contato" (edição de contato) nada é obrigatório — os demais
// campos ficam bloqueados e clientes antigos sem gênero não podem ser travados.
const ClientFormSchema = (readOnly: boolean): FormRules =>
	readOnly ? {} : { gender: [FormValidations.IsChoose] };

export const ClientForm = defineComponent({
	name: 'ClientForm',

	extends: defineForm<ClientFormData>(),

	props: {
		/** Quando true, apenas nome, email e telefone são editáveis (demais campos bloqueados). */
		onlyNameAndContactEditable: { type: Boolean, default: false },
		onPhoneConflict: {
			type: Function as PropType<(info: PhoneConflictInfo) => void>,
			default: undefined,
		},
	},

	data() {
		return {
			initialEmail: undefined as string | undefined,
			initialPhone: undefined as string | undefined,
		};
	},

	created() {
		const contact = (this as any).formData?.contact;
		if (contact) {
			this.initialEmail = contact.email;
			this.initialPhone = contact.phone;
		}
	},

	methods: {
		async searchRenter() {
			const response = await PublicService.getRenter(this.formData.document);
			if (response.data) {
				nextTick(() => {
					this.formData = response.data;
				});
			}
		},
	},

	render() {
		const readOnly = this.onlyNameAndContactEditable;
		return (
			<NForm
				size="large"
				ref="formRef"
				{...{ attrs: this.$attrs }}
				label-placement="top"
				rules={ClientFormSchema(readOnly)}
				model={this.formData}
				onSubmit={this.handleSubmit}
			>
				<div class="mt-2">
					<NFormItem label="Tipo de pessoa" path="personType">
						<NRadioGroup v-model:value={this.formData.personType} disabled={readOnly}>
							<NRadio label="Pessoa física" value={PersonType.Natural} />
							<NRadio label="Pessoa jurídica" value={PersonType.LegalPerson} />
						</NRadioGroup>
					</NFormItem>

					<NGrid cols="1 400:2" x-gap="4" y-gap="4">
						<NFormItemGi label="CPF" path="document">
							<AppInputCpf
								data-cy="input-document"
								onChange={readOnly ? undefined : this.searchRenter}
								v-model={this.formData.document}
								readonly={readOnly}
							/>
						</NFormItemGi>

						<NFormItemGi label="Nome completo" path="name">
							<NInput
								data-cy="input-name"
								v-model:value={this.formData.name}
								placeholder="Nome Completo"
								disabled={readOnly}
							/>
						</NFormItemGi>

						<NFormItemGi label="Data de nascimento" path="birthDate">
							<AppInputDate data-cy="input-birthDate" v-model={this.formData.birthDate} disabled={readOnly} />
						</NFormItemGi>

						{this.formData.personType != PersonType.LegalPerson && (
							<NFormItemGi label="Gênero" path="gender">
								<NRadioGroup v-model:value={this.formData.gender} disabled={readOnly}>
									<NRadio data-cy="input-gender-male" label="Masculino" value={Gender.MALE} />
									<NRadio data-cy="input-gender-female" label="Feminino" value={Gender.FEMALE} />
								</NRadioGroup>
							</NFormItemGi>
						)}
						<NFormItemGi label="País de origem" path="nationality">
							<NSelect
								data-cy="input-nationality"
								v-model:value={this.formData.nationality}
								options={nationalityOptions}
								placeholder="Selecione o país de origem"
								filterable
								disabled={readOnly}
							/>
						</NFormItemGi>

						<NFormItemGi label="Renda declarada" path="declaredIncome">
							<AppInputMoney
								data-cy="input-declaredIncome"
								v-model={this.formData.declaredIncome}
								disabled={readOnly}
							/>
						</NFormItemGi>

						<NFormItemGi label="Ocupação" path="occupation">
							<NSelect
								v-model:value={this.formData.occupation}
								disabled={readOnly}
								options={[
									{ label: 'Assalariado', value: 'Assalariado' },
									{
										label: 'Autonomo/Profissional liberal',
										value: 'Autonomo/Profissional liberal',
									},
									{ label: 'Empresário', value: 'Empresário' },
									{ label: 'Aposentado', value: 'Aposentado' },
									{ label: 'Estudante', value: 'Estudante' },
								]}
							/>
						</NFormItemGi>

						{this.formData.personType == PersonType.LegalPerson && [
							<NFormItemGi label="Data de abertura" path="openingDate">
								<AppInputDate data-cy="input-openingDate" v-model={this.formData.openingDate} disabled={readOnly} />
							</NFormItemGi>,
							<NFormItemGi label="CNPJ" path="cnpj">
								<AppInputCnpj data-cy="input-cnpj" v-model={this.formData.cnpj} readonly={readOnly} />
							</NFormItemGi>,
							<NFormItemGi label="Razão Social" path="fantasyName">
								<NInput
									data-cy="input-name"
									v-model:value={this.formData.fantasyName}
									placeholder="Razão Social"
									disabled={readOnly}
								/>
							</NFormItemGi>,
						]}
					</NGrid>

					<ContactForm
						ref="formContactRef"
						v-model={this.formData.contact}
						renterId={(this.formData as any).id}
						originalEmail={this.initialEmail}
						originalPhone={this.initialPhone}
						onPhoneConflict={this.onPhoneConflict}
					/>
				</div>
			</NForm>
		);
	},
});
